'use client';

import type { FormikErrors } from 'formik';
import { Form, Formik } from 'formik';
import { Button, Grid } from '@petsmart-ui/sparky';
import type { Nullable } from '@libs/core';
import { apiFetch, AUTH_COOKIES, COOKIES, DeliveryType, OG_COOKIE, PROCEED_TO_CHECKOUT_PRESSED } from '@libs/core';
import type { IdentityUser } from '@libs/common-next';
import {
    AccountDrawerType,
    clsx,
    decodeJwtCookie,
    getNextApiRoute,
    GoogleRecaptchaCheckbox,
    PageType,
    recaptchaId,
    useCart,
    useCartNotifications,
    useGoogleRecaptcha,
    usePageLayout,
    useUserSession,
    logSentryError,
    SentryAcpSignInErrors as SentryErrors,
    useLocalStorage,
    hasFulfillmentMethodInCart,
    trackAuthenticationAttempt,
    useSentryTrackingContext,
    useDecisions,
} from '@libs/common-next';
import { useEffect, useMemo, useState } from 'react';

import { usePathname, useSearchParams } from 'next/navigation';
import type { IdentityRouteAuthMergeResponse } from '@libs/experience-api';
import { NEW_TAB, PRIMARY_BUTTON_STYLE, SECONDARY_BUTTON_STYLE, TARGET_BLANK, TARGET_SELF } from '@libs/contentful';
import type { TreatsLoginLocation } from '@libs/analytics';
import { getAutoshipSubscriptions, trackError, useLoginEvents } from '@libs/analytics';
import Cookies from 'js-cookie';
import type { CartUI, CartValidationResponse } from '@libs/common-node';
import { ValidationAlerts } from '../ValidationAlert';
import { EmailField } from './EmailField';
import { validateSignInForm } from './schema';
import type { SignInFormValues } from './types';
import type { InterstitialCta, InterstitialLinkAction } from '../InterstitialSignIn';
import { PasswordField } from './PasswordField';
import { ForgotPasswordCta } from './ForgotPasswordCta';
import {
    ACTIVATE_OFFER_PAGE,
    CART_PAGE,
    CHECKOUT_PAGE,
    CREATE_ACCOUNT_PAGE,
    emailExistsMsg,
    SIGN_IN_PAGE,
    TREATS_REWARDS_AUTHENTICATED_PAGE,
    TREATS_REWARDS_PAGE,
    RESET_PASSWORD,
} from './constants';
import { showMergedCartBanner, SnackbarUtils, windowRedirect } from '../../utils';

import './SignInForm.scss';
import { KeepSignedInCheckbox } from '../KeepSignedInCheckbox';

const initialFormState: SignInFormValues = {
    email: '',
    password: '',
    isExistingEmail: false,
};

export type SignInFormProps = {
    cta?: InterstitialCta;
    linkAction?: InterstitialLinkAction;
    isAutoship?: boolean;
    isInDrawer?: boolean;
    redirectTo?: string;
    location?: TreatsLoginLocation | null;
    pathOverride?: string;
    onSuccessfulSignIn?: () => Promise<void> | void;
};

const componentClassname = 'sign-in-form';

const pagesToRedirect = [TREATS_REWARDS_PAGE, CREATE_ACCOUNT_PAGE, SIGN_IN_PAGE, ACTIVATE_OFFER_PAGE, RESET_PASSWORD];

// TODO: try to replace with useRef to prevent re-render.  Had trouble unit testing useRef  Or possibly try window !== undefined
function useIsRendered() {
    const [isRendered, setIsRendered] = useState(false);

    useEffect(() => {
        setIsRendered(true);
    }, []);

    return isRendered;
}

export function SignInForm({
    cta,
    linkAction,
    isInDrawer = false,
    location = null,
    pathOverride,
    onSuccessfulSignIn,
}: Readonly<SignInFormProps>) {
    const [isFetching, setIsFetching] = useState(false);
    const [authResponse, setAuthResponse] = useState<IdentityRouteAuthMergeResponse | null>(null);
    const [emailExists, setEmailExists] = useState(false);
    const { soft_login: isSoftLoginEnabled } = useDecisions();

    // Get Sentry tracking context
    const sentryContext = useSentryTrackingContext();

    const searchParams = useSearchParams();
    const redirectUrl = searchParams?.get('redirectUrl') ?? null;

    const isRendered = useIsRendered();

    const { accountDrawersOpen, isAccountDrawerOpen, flags, removeAccountDrawers, pageType } = usePageLayout();
    const { setUser } = useUserSession();
    const {
        handleRecaptcha,
        useRecaptchaCheckbox,
        setUseRecaptchaCheckbox,
        resetRecaptchaCheckbox,
        resetRecaptchaValues,
    } = useGoogleRecaptcha(!!flags.loginRecaptchaEnabled);
    const { setCart, refetchCart, updateSubscriptionStatus } = useCart();
    const { setNotifications: setCartNotifications } = useCartNotifications();
    const [proceedToCheckoutPressed, setProceedToCheckoutPressed] = useLocalStorage<boolean | undefined>(
        PROCEED_TO_CHECKOUT_PRESSED
    );
    const autoshipDiscountUpdatesEnabled = Boolean(flags?.autoshipDiscountUpdatesEnabled);

    const pathname = usePathname();
    const isCheckoutLogin = pathname?.includes('checkout-login');

    const idPrefix = useMemo(() => (isInDrawer ? 'sign-in-drawer-' : ''), [isInDrawer]);

    const { trackSuccessSignIn } = useLoginEvents();

    const shouldRedirectToCheckout = (updatedCart: CartUI) => {
        if (
            proceedToCheckoutPressed &&
            hasFulfillmentMethodInCart(updatedCart, DeliveryType.AUTOSHIP) &&
            !updatedCart.hasAutoshipOrders &&
            autoshipDiscountUpdatesEnabled
        ) {
            setProceedToCheckoutPressed(undefined);

            return true;
        }
    };

    function handleRedirect(isCartValidationSuccessful: boolean, updatedCart?: Nullable<CartUI>) {
        const isCartPage = pageType === PageType.CART;
        const isCheckoutPage = pageType === PageType.CHECKOUT;

        if (isCartPage || isCheckoutPage) {
            if (isCartValidationSuccessful) {
                const hasNotifications = updatedCart?.notifications?.length;

                if (hasNotifications) {
                    if (isCartPage) {
                        return;
                    }

                    if (isCheckoutLogin) {
                        windowRedirect(CART_PAGE);
                        return;
                    }
                }

                if (isCartPage && updatedCart?.id) {
                    if (shouldRedirectToCheckout(updatedCart)) {
                        windowRedirect(CHECKOUT_PAGE);
                        return;
                    }

                    // No action needed on the cart page because updated cart
                    // is already in state after successful login.
                    return;
                }

                // TODO: convert to router.push once upgrade to Next 15
                windowRedirect(CHECKOUT_PAGE);
                return;
            }

            // we are forcing refresh in order to display merged cart,
            // should be improved once better solution is found
            // TODO: convert to router.push once upgrade to Next 15
            windowRedirect(CART_PAGE);
            return;
        }

        if (pathname === SIGN_IN_PAGE && redirectUrl) {
            windowRedirect(redirectUrl);
            return;
        }
        if (pathname && pagesToRedirect.some(page => pathname.startsWith(page))) {
            // TODO: convert to router.push once upgrade to Next 15
            windowRedirect(TREATS_REWARDS_AUTHENTICATED_PAGE);
        }
    }

    function handleInvalidSubmit({ email, password }: { email?: string[]; password?: string[] }) {
        const errorMessage = [...(email ?? []), ...(password ?? [])];
        if (emailExists) {
            errorMessage.push(emailExistsMsg);
        }
        trackError('login', errorMessage.join(' | '));
    }

    function handleApiFail() {
        SnackbarUtils.showPredefined('SERVER_ERROR');
    }

    const checkboxId = isInDrawer ? recaptchaId.loginDrawerCheckbox : recaptchaId.loginCheckbox;
    const scoreId = isInDrawer ? recaptchaId.loginDrawerScore : recaptchaId.loginScore;
    const isDrawerOpen = accountDrawersOpen?.includes(AccountDrawerType.SIGN_IN);
    const isDrawerFormOpen = isInDrawer && isDrawerOpen;
    const isNonDrawerFormOpen = !isInDrawer && !isAccountDrawerOpen;

    // TODO: consider moving some of this logic up into useGoogleRecaptcha hook
    useEffect(() => {
        setAuthResponse(null);
        setUseRecaptchaCheckbox(null);
        resetRecaptchaValues();
    }, [isDrawerFormOpen, isNonDrawerFormOpen]);

    async function validateCartMerge(cartId?: string): Promise<boolean> {
        if (!cartId) {
            return false;
        }

        try {
            const result = await apiFetch<CartValidationResponse>(
                getNextApiRoute(`carts/${cartId}/validateCartMerge`),
                { method: 'PUT' }
            );

            if (result instanceof Error || ('status' in result && result.status === 500)) {
                handleApiFail();
                return false;
            }

            if (!result) {
                return false;
            }

            return (result && 'id' in result) || (result && 'size' in result && 'timeout' in result);
        } catch (error) {
            logSentryError(error, SentryErrors.SignInForm.ValidateCartMerge);
            return false;
        }
    }

    async function handleSubmitForm(values: SignInFormValues) {
        if (isFetching) return; // prevent double submit

        // prevent multiple sign in from multiple browser tabs
        const accessToken = Cookies.get(AUTH_COOKIES.ACCESS_TOKEN);
        if (accessToken) {
            const updatedCart = await refetchCart();
            await handleSuccessfulSignIn(false, updatedCart);
            return;
        }

        setIsFetching(true);

        try {
            const resultRecaptcha = await handleRecaptcha(scoreId);
            if (!resultRecaptcha) {
                setIsFetching(false);
                return;
            }

            const res = await apiFetch<IdentityRouteAuthMergeResponse>(getNextApiRoute(`auth`), {
                method: 'POST',
                body: JSON.stringify({
                    email: values.email,
                    password: values.password,
                }),
                credentials: 'include',
                additionalSuccessCodes: [401],
            });

            if (res instanceof Error || ('status' in res && res.status === 500)) {
                handleApiFail();

                throw new Error('Identity API error during sign in');
            }

            if ('success' in res) {
                setAuthResponse(res);
                setUser(decodeJwtCookie<IdentityUser>(AUTH_COOKIES.ACCESS_TOKEN));

                let updatedCart = res?.data?.updatedCart ?? res?.data?.cart;

                if (updatedCart?.id && updatedCart.version) {
                    if (autoshipDiscountUpdatesEnabled) {
                        const cartWithUpdatedSubscriptionStatus = await updateSubscriptionStatus(updatedCart.id);

                        if (cartWithUpdatedSubscriptionStatus?.id) {
                            updatedCart = {
                                ...cartWithUpdatedSubscriptionStatus,
                                notifications: updatedCart.notifications,
                            };
                        }
                    }

                    showMergedCartBanner(Cookies.get(COOKIES.CART_ID), updatedCart);
                    Cookies.set(COOKIES.CART_ID, updatedCart.id);
                    setCart(updatedCart);

                    if (updatedCart.notifications?.length) {
                        setCartNotifications(updatedCart.notifications);
                    }
                }

                if (res?.success) {
                    // Track successful authentication
                    trackAuthenticationAttempt(
                        'email_password',
                        {
                            success: true,
                            recaptcha_score: typeof resultRecaptcha === 'number' ? resultRecaptcha : undefined,
                        },
                        sentryContext
                    );

                    const isCartMergeSuccessful =
                        pageType === PageType.CART || pageType === PageType.CHECKOUT
                            ? await validateCartMerge(updatedCart?.id)
                            : false;
                    await handleSuccessfulSignIn(isCartMergeSuccessful, updatedCart);
                } else {
                    // Track authentication failure
                    trackAuthenticationAttempt(
                        'email_password',
                        {
                            success: false,
                            failure_reason: res?.message?.header ?? 'Identity API error during sign in',
                            error_code: 'UNKNOWN_ERROR',
                        },
                        sentryContext
                    );

                    throw new Error(res?.message?.header ?? 'Identity API error during sign in');
                }
            } else {
                // Track authentication failure for non-success responses
                trackAuthenticationAttempt(
                    'email_password',
                    {
                        success: false,
                        failure_reason: 'Authentication failed - no success response',
                        error_code: 'NO_SUCCESS_RESPONSE',
                    },
                    sentryContext
                );
            }
        } catch (err) {
            logSentryError(err, SentryErrors.SignInForm.SubmitFailed);

            // Track authentication failure for caught errors
            trackAuthenticationAttempt(
                'email_password',
                {
                    success: false,
                    failure_reason: err instanceof Error ? err.message : 'Unknown error during sign in',
                    error_code: 'SIGNIN_FORM_ERROR',
                },
                sentryContext
            );

            if (useRecaptchaCheckbox) {
                resetRecaptchaCheckbox('failed');
            } else {
                setUseRecaptchaCheckbox(checkboxId);
            }
        } finally {
            setIsFetching(false);
        }
    }

    async function handleSuccessfulSignIn(isCartMergeSuccessful: boolean, updatedCart?: Nullable<CartUI>) {
        setUser(decodeJwtCookie<IdentityUser>(AUTH_COOKIES.ACCESS_TOKEN));

        setUseRecaptchaCheckbox(null);

        await onSuccessfulSignIn?.();

        removeAccountDrawers([AccountDrawerType.SIGN_IN]);

        const autoshipSubscriptions = await getAutoshipSubscriptions();

        const isFirstAutoshipEligible = autoshipSubscriptions?.count === 0;

        /**
         * Remove Order Groove cookie in order to avoid potential issues
         */
        Cookies.remove(OG_COOKIE);

        const callback = () => {
            handleRedirect(isCartMergeSuccessful, updatedCart);
        };
        trackSuccessSignIn(location, pathOverride, isFirstAutoshipEligible, callback);
    }

    // biome-ignore lint/correctness/noNestedComponentDefinitions: todo: fix this
    function RenderCTA({
        validateForm,
    }: {
        validateForm: (values?: unknown) => Promise<FormikErrors<SignInFormValues>>;
    }) {
        const buttonType = isRendered ? 'submit' : 'button';
        const buttonStyle = cta?.fields.buttonStyle.toLowerCase();
        const ctaVariant = buttonStyle === 'primary' ? PRIMARY_BUTTON_STYLE : SECONDARY_BUTTON_STYLE;
        const buttonProps = {
            'data-testid': 'signInCta',
            fullWidth: true,
            hideText: isFetching,
            loading: isFetching,
            text: cta?.fields?.text ?? 'Sign in',
            type: buttonType as typeof buttonType,
        };

        return cta ? (
            <a
                target={cta.fields.behavior === NEW_TAB ? TARGET_BLANK : TARGET_SELF}
                href={cta?.fields?.link?.fields?.webUrl ?? '#'}
                rel="noreferrer"
            >
                <Button {...buttonProps} size="lg" variant={ctaVariant} />
            </a>
        ) : (
            <Button
                {...buttonProps}
                onClick={async () => {
                    if (isRendered) {
                        const result = await validateForm();

                        if (Object.keys(result).length > 0) {
                            handleInvalidSubmit(
                                result as {
                                    [key: string]: string[];
                                }
                            );
                        }
                    }
                }}
                size="lg"
                variant="primary"
            />
        );
    }

    return (
        <Formik
            onSubmit={handleSubmitForm}
            initialValues={initialFormState}
            validate={validateSignInForm}
            validateOnBlur
        >
            {({ validateForm }) => (
                <Form noValidate>
                    {authResponse?.data?.code === 401 && (
                        <ValidationAlerts
                            defaultHeading={authResponse.message?.header}
                            validationErrors={[{ message: authResponse.message?.body }]}
                        />
                    )}
                    <Grid gap="md">
                        <Grid.Item
                            className={clsx(
                                `${componentClassname}`,
                                isCheckoutLogin && `${componentClassname}--checkout`
                            )}
                        >
                            <EmailField
                                uniqueId={`${idPrefix}email`}
                                emailExists={emailExists}
                                setEmailExists={setEmailExists}
                            />
                            <PasswordField data-testid="testingId" uniqueId={`${idPrefix}password`} />
                            {useRecaptchaCheckbox && (isDrawerFormOpen || isNonDrawerFormOpen) && (
                                <GoogleRecaptchaCheckbox
                                    id={checkboxId}
                                    page={pageType}
                                    parent={`SignInForm${isInDrawer ? `_SignInDrawer` : ``}`}
                                />
                            )}
                            {isSoftLoginEnabled && <KeepSignedInCheckbox />}
                        </Grid.Item>
                        <Grid.Item className={`${componentClassname}__cta`}>
                            <RenderCTA {...{ validateForm }} />
                        </Grid.Item>

                        <ForgotPasswordCta
                            componentClassname={componentClassname}
                            webUrl={linkAction?.fields?.link?.webUrl}
                            actionText={linkAction?.fields?.actionText}
                        />
                    </Grid>
                </Form>
            )}
        </Formik>
    );
}
